import {
  type ResolvedColorScheme,
  setColorScheme,
  useColorScheme,
} from '@dashhudson/dashing-ui/use/color-scheme';
import { watch } from 'vue';
import { useRoute } from 'vue-router';

import { useIdentityStore } from '@/stores/identity';

const COLOR_SCHEME_QUERY_PARAM = 'theme';

// No `auto`: it resolves through `prefers-color-scheme` for JS consumers, so
// dashing-ui's `BaseChart` would paint dark colours onto a light page.
const SCHEMES: readonly ResolvedColorScheme[] = ['light', 'dark'];

const asScheme = (value: unknown): ResolvedColorScheme | null =>
  SCHEMES.includes(value as ResolvedColorScheme) ? (value as ResolvedColorScheme) : null;

/**
 * Lets Dash Social staff preview the unreleased dark scheme via `?theme=dark|light`.
 *
 * Non-staff are pinned to `light` rather than ignored, since the preference is
 * persisted — a stored `dark` must not leak the unfinished scheme to a customer.
 *
 * Call once, from the app root.
 */
export function useColorSchemePreview(): void {
  const identityStore = useIdentityStore();
  const route = useRoute();

  watch(
    [() => identityStore.identity, () => route.query[COLOR_SCHEME_QUERY_PARAM]],
    ([identity, requested]) => {
      // Null means "not loaded yet", not "not staff" — pinning here would undo
      // `applyStoredColorScheme()` on every boot.
      if (!identity) return;

      if (!identityStore.isDashSocialUser) {
        setColorScheme('light');
        return;
      }

      const scheme = asScheme(requested);
      if (scheme) {
        setColorScheme(scheme);
        return;
      }

      // Nothing to write, but the singleton is what mirrors the scheme into the
      // token cache canvas code reads.
      useColorScheme();
    },
    { immediate: true },
  );
}
